'use client';

import FullWidthSection from '@/components/full-width-section';
import ImageOptimizer from '@/components/image-optimizer';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { useBannerList } from '@/services/query/banner.query';
import { BannerPosition } from '@/services/types/banner.type';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const BannerCarousel = () => {
  const [pcApi, setPcApi] = useState<CarouselApi>();
  const [mobileApi, setMobileApi] = useState<CarouselApi>();
  const [pcCurrentSlide, setPcCurrentSlide] = useState(0);

  const { data: bannerList } = useBannerList({
    position: BannerPosition.TOP,
  });
  const pcMainBanners = Array.isArray(bannerList?.data?.pc)
    ? [...bannerList.data.pc, ...bannerList.data.pc]
    : [];
  const mobileMainBanners = bannerList?.data?.mobile ?? [];

  useEffect(() => {
    if (!pcApi) return;

    const onPcSelect = () => {
      // setTimeout(() => {
      setPcCurrentSlide(pcApi.selectedScrollSnap());
      // }, 300);
    };
    pcApi.on('select', onPcSelect);

    return () => {
      pcApi.off('select', onPcSelect);
    };
  }, [pcApi]);

  const handlePrevClick = () => {
    pcApi?.scrollPrev();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  const handleNextClick = () => {
    pcApi?.scrollNext();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  return (
    <>
      {/**웹 */}
      <div className="desktop:block hidden">
        {pcMainBanners?.length > 0 && (
          <>
            <FullWidthSection>
              <section className="mx-auto mb-25">
                <div className="relative">
                  <Carousel
                    setApi={setPcApi}
                    opts={{
                      align: 'center',
                      loop: true,
                      slidesToScroll: 1,
                      containScroll: 'trimSnaps',
                    }}
                    plugins={[
                      Autoplay({
                        delay: 3000,
                        stopOnMouseEnter: true,
                        stopOnInteraction: false,
                      }),
                    ]}
                  >
                    <CarouselContent className="mx-auto flex items-center">
                      {pcMainBanners.map((banner, idx) => (
                        <CarouselItem
                          key={idx}
                          className={`flex-shrink-0 basis-[1180px] pl-0 ${
                            pcCurrentSlide === idx
                              ? 'h-[480px] w-[1180px]'
                              : 'h-[420px] w-[1030px] px-15'
                          }`}
                        >
                          <div className="flex h-full space-x-0 px-0">
                            <Link
                              href={banner.href || ''}
                              key={banner.id}
                              className="relative h-full w-full overflow-hidden rounded-xl"
                            >
                              <ImageOptimizer
                                src={banner.upfilePath}
                                alt={banner.title}
                                fill
                                className="object-cover"
                              />
                            </Link>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>
              </section>
            </FullWidthSection>

            <div className="web-w relative mx-auto">
              <Button
                onClick={handlePrevClick}
                disabled={!pcApi}
                className="border-stroke-white absolute -top-[640px] right-18 z-10 flex size-[55px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border bg-white/80 p-0 hover:bg-gray-100"
              >
                <ChevronLeft className="size-6" />
              </Button>
              <Button
                onClick={handleNextClick}
                disabled={!pcApi}
                className="border-stroke-white absolute -top-[640px] right-1 flex size-[55px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border bg-white/80 p-0 hover:bg-gray-100"
              >
                <ChevronRight className="size-6" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/**모바일 */}
      <section className="desktop:hidden mx-auto mb-15">
        {mobileMainBanners?.length > 0 && (
          <div className="relative">
            <Carousel
              setApi={setMobileApi}
              opts={{
                align: 'start',
                loop: true,
                slidesToScroll: 1,
                containScroll: 'trimSnaps',
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                  stopOnMouseEnter: true,
                  stopOnInteraction: false,
                }),
              ]}
            >
              <CarouselContent>
                {mobileMainBanners.map((banner, idx) => (
                  <CarouselItem key={idx} className="flex-shrink-0">
                    <div className="flex space-x-0">
                      <Link
                        href={banner.href || ''}
                        key={banner.id}
                        className="relative aspect-[452/240] w-full overflow-hidden rounded-xl"
                      >
                        <ImageOptimizer
                          src={banner.upfilePath}
                          alt={banner.title}
                          fill
                          className="object-cover"
                        />
                      </Link>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            <Button
              onClick={() => mobileApi?.scrollPrev()}
              disabled={!mobileApi}
              className="border-stroke-white absolute top-1/2 -left-5 flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border bg-white/80 p-0 hover:bg-gray-100"
            >
              <ChevronLeft className="size-4.5" strokeWidth={1.5} />
            </Button>
            <Button
              onClick={() => mobileApi?.scrollNext()}
              disabled={!mobileApi}
              className="border-stroke-white absolute top-1/2 -right-5 flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border bg-white/80 p-0 hover:bg-gray-100"
            >
              <ChevronRight className="size-4.5" strokeWidth={1.5} />
            </Button>
          </div>
        )}
      </section>
    </>
  );
};

export default BannerCarousel;
